import React, { useState, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
} from '@heroicons/react/24/outline';
import { FONT_FAMILIES, FONT_SIZES, LINE_SPACING } from '../modals/TextEditor/constants';
import {
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  AlignTopIcon,
  AlignMiddleIcon,
  AlignBottomIcon,
} from '../modals/TextEditor/components/AlignmentIcons';

export default function TextElementControlPanel({
  content,
  style,
  onContentChange,
  onStyleChange,
}) {
  const [localContent, setLocalContent] = useState(content || '');

  useEffect(() => {
    setLocalContent(content || '');
  }, [content]);

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setLocalContent(newContent);
    onContentChange?.(newContent);
  };

  const toggleStyle = (property, value) => {
    onStyleChange?.({
      [property]: style[property] === value ? undefined : value,
    });
  };

  return (
    <div className="space-y-4 pt-4 border-t border-gray-200">
      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Text Content</h3>
      
      {/* Text Content Textarea */}
      <textarea
        value={localContent}
        onChange={handleContentChange}
        className="w-full px-2 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
        placeholder="Enter text content..."
        rows={3}
      />

      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Font</h3>
        
        {/* Font Family */}
        <div className="mb-3">
          <label className="text-xs font-medium text-gray-700 block mb-1">Family</label>
          <select
            value={style?.fontFamily || 'Arial'}
            onChange={(e) => onStyleChange?.({ fontFamily: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {FONT_FAMILIES.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </select>
        </div>

        {/* Font Size */}
        <div className="mb-3">
          <label className="text-xs font-medium text-gray-700 block mb-1">Size</label>
          <select
            value={style?.fontSize || '16px'}
            onChange={(e) => onStyleChange?.({ fontSize: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {FONT_SIZES.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
        </div>

        {/* Line Height */}
        <div className="mb-3">
          <label className="text-xs font-medium text-gray-700 block mb-1">Line Height</label>
          <select
            value={style?.lineHeight || '1'}
            onChange={(e) => onStyleChange?.({ lineHeight: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {LINE_SPACING.map((spacing) => (
              <option key={spacing.value} value={spacing.value}>
                {spacing.label}
              </option>
            ))}
          </select>
        </div>

        {/* Text Color */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-3">Color</label>
          <div className="w-full">
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <HexColorPicker
                color={style?.color || '#000000'}
                onChange={(color) => onStyleChange?.({ color })}
                style={{ width: '100%', height: '200px' }}
              />
              <div className="mt-4 flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">HEX</span>
                <input
                  type="text"
                  value={(style?.color || '#000000').toUpperCase()}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-F]{6}$/i.test(val)) {
                      onStyleChange?.({ color: val });
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Text Formatting */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Formatting</h3>
        
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => toggleStyle('fontWeight', 'bold')}
            disabled={style?.fontFamily === 'Bangers'}
            title={style?.fontFamily === 'Bangers' ? 'Bold not supported for Bangers font' : 'Bold'}
            className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              style?.fontWeight === 'bold'
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <BoldIcon className="h-4 w-4 mx-auto" />
          </button>

          <button
            onClick={() => toggleStyle('fontStyle', 'italic')}
            disabled={style?.fontFamily === 'Bangers'}
            title={style?.fontFamily === 'Bangers' ? 'Italic not supported for Bangers font' : 'Italic'}
            className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              style?.fontStyle === 'italic'
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <ItalicIcon className="h-4 w-4 mx-auto" />
          </button>

          <button
            onClick={() => toggleStyle('textDecoration', 'underline')}
            className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              style?.textDecoration === 'underline'
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
            }`}
          >
            <UnderlineIcon className="h-4 w-4 mx-auto" />
          </button>
        </div>
      </div>

      {/* Text Alignment */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Alignment</h3>
        
        {/* Horizontal Alignment */}
        <div className="mb-3">
          <label className="text-xs font-medium text-gray-700 block mb-2">Horizontal</label>
          <div className="flex gap-2">
            <button
              onClick={() => toggleStyle('textAlign', 'left')}
              className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
                style?.textAlign === 'left'
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <AlignLeftIcon className="h-4 w-4 mx-auto" />
            </button>

            <button
              onClick={() => toggleStyle('textAlign', 'center')}
              className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
                style?.textAlign === 'center'
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <AlignCenterIcon className="h-4 w-4 mx-auto" />
            </button>

            <button
              onClick={() => toggleStyle('textAlign', 'right')}
              className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
                style?.textAlign === 'right'
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <AlignRightIcon className="h-4 w-4 mx-auto" />
            </button>
          </div>
        </div>

        {/* Vertical Alignment */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-2">Vertical</label>
          <div className="flex gap-2">
            <button
              onClick={() => toggleStyle('verticalAlign', 'top')}
              className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
                style?.verticalAlign === 'top'
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <AlignTopIcon className="h-4 w-4 mx-auto" />
            </button>

            <button
              onClick={() => toggleStyle('verticalAlign', 'middle')}
              className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
                style?.verticalAlign === 'middle'
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <AlignMiddleIcon className="h-4 w-4 mx-auto" />
            </button>

            <button
              onClick={() => toggleStyle('verticalAlign', 'bottom')}
              className={`flex-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
                style?.verticalAlign === 'bottom'
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <AlignBottomIcon className="h-4 w-4 mx-auto" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
